import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, RefreshCw, CheckCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import HOSLogo from '../../components/HOSLogo';

/* ── Simple math captcha ── */
function generateCaptcha() {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  const ops = ['+', '-', '×'];
  const op  = ops[Math.floor(Math.random() * ops.length)];
  let answer;
  if (op === '+') answer = a + b;
  else if (op === '-') answer = Math.abs(a - b);
  else answer = a * b;
  const q = op === '-' ? `${Math.max(a,b)} − ${Math.min(a,b)}` : `${a} ${op} ${b}`;
  return { question: q, answer: String(answer) };
}

export default function AdminRegister() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: '', middleName: '', lastName: '',
    email: '', phone: '', password: '', confirmPassword: '',
  });
  const [captcha, setCaptcha]       = useState(generateCaptcha);
  const [captchaInput, setCaptchaInput] = useState('');
  const [showPw, setShowPw]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [errors, setErrors]         = useState({});

  // Only redirect if auth is fully resolved AND user is already master_admin
  useEffect(() => {
    if (!authLoading && user?.role === 'master_admin') {
      navigate('/admin/profile', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const refreshCaptcha = () => { setCaptcha(generateCaptcha()); setCaptchaInput(''); };

  // Show spinner while auth resolves (prevents flash before redirect)
  if (authLoading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const validate = () => {
    const e = {};
    if (!form.firstName.trim())  e.firstName  = 'First name is required';
    if (!form.lastName.trim())   e.lastName   = 'Last name is required';
    if (!form.email.trim())      e.email      = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email address';
    if (!form.phone.trim())      e.phone      = 'Phone number is required';
    if (!form.password)          e.password   = 'Password is required';
    else if (form.password.length < 6) e.password = 'Minimum 6 characters';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (!captchaInput.trim())    e.captcha    = 'Please solve the captcha';
    else if (captchaInput.trim() !== captcha.answer) e.captcha = 'Incorrect answer — try again';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      if (errs.captcha) refreshCaptcha();
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const name = [form.firstName, form.middleName, form.lastName].filter(Boolean).join(' ');
      await api.post('/auth/register', {
        name,
        firstName:  form.firstName,
        middleName: form.middleName,
        lastName:   form.lastName,
        email:      form.email,
        phone:      form.phone,
        password:   form.password,
        role:       'master_admin',
      });
      toast.success('Master Admin account created! Please sign in.');
      navigate('/admin/login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
      refreshCaptcha();
    } finally { setLoading(false); }
  };

  const set = (key) => (e) => {
    setForm(f => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors(er => ({ ...er, [key]: '' }));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Top bar */}
          <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #B8A830, #d4af37, #B8A830)' }} />

          <div className="p-8">
            {/* Logo + heading */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4"><HOSLogo size={40} /></div>
              <h1 className="text-2xl font-bold text-gray-800">Create Master Admin Account</h1>
              <p className="text-sm text-gray-400 mt-1">Fill in your details to register</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {/* Name row */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'First Name *', key: 'firstName', placeholder: 'First' },
                  { label: 'Middle Name',  key: 'middleName', placeholder: 'Middle' },
                  { label: 'Last Name *',  key: 'lastName',  placeholder: 'Last' },
                ].map(({ label, key, placeholder }) => (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
                    <input value={form[key]} onChange={set(key)} placeholder={placeholder}
                      className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors
                        ${errors[key] ? 'border-rose-400 bg-rose-50' : 'border-gray-200'}`} />
                    {errors[key] && <p className="text-xs text-rose-500 mt-0.5">{errors[key]}</p>}
                  </div>
                ))}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Email Address *</label>
                <input value={form.email} onChange={set('email')} type="email" placeholder="admin@example.com"
                  className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors
                    ${errors.email ? 'border-rose-400 bg-rose-50' : 'border-gray-200'}`} />
                {errors.email && <p className="text-xs text-rose-500 mt-0.5">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Phone Number *</label>
                <input value={form.phone} onChange={set('phone')} type="tel" placeholder="+91 00000 00000"
                  className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors
                    ${errors.phone ? 'border-rose-400 bg-rose-50' : 'border-gray-200'}`} />
                {errors.phone && <p className="text-xs text-rose-500 mt-0.5">{errors.phone}</p>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Password *</label>
                <div className="relative">
                  <input value={form.password} onChange={set('password')}
                    type={showPw ? 'text' : 'password'} placeholder="Min. 6 characters"
                    className={`w-full px-3 py-2.5 pr-10 border rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors
                      ${errors.password ? 'border-rose-400 bg-rose-50' : 'border-gray-200'}`} />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-rose-500 mt-0.5">{errors.password}</p>}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Re-enter Password *</label>
                <div className="relative">
                  <input value={form.confirmPassword} onChange={set('confirmPassword')}
                    type={showConfirm ? 'text' : 'password'} placeholder="Repeat password"
                    className={`w-full px-3 py-2.5 pr-10 border rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors
                      ${errors.confirmPassword ? 'border-rose-400 bg-rose-50' : 'border-gray-200'}`} />
                  <button type="button" onClick={() => setShowConfirm(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-xs text-rose-500 mt-0.5">{errors.confirmPassword}</p>}
                {form.password && form.confirmPassword && !errors.confirmPassword && (
                  <p className={`text-xs mt-0.5 flex items-center gap-1 ${form.password === form.confirmPassword ? 'text-emerald-600' : 'text-rose-500'}`}>
                    <CheckCircle size={11} />
                    {form.password === form.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                  </p>
                )}
              </div>

              {/* Captcha */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Security Check *</label>
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1 bg-gray-900 text-white rounded-xl px-4 py-3 text-center font-mono text-lg font-bold tracking-widest select-none"
                    style={{ letterSpacing: '0.3em', textDecoration: 'line-through', textDecorationColor: 'rgba(255,255,255,0.2)' }}>
                    {captcha.question} = ?
                  </div>
                  <button type="button" onClick={refreshCaptcha}
                    className="p-2.5 rounded-xl border border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 transition-colors">
                    <RefreshCw size={14} />
                  </button>
                </div>
                <input value={captchaInput} onChange={e => { setCaptchaInput(e.target.value); if (errors.captcha) setErrors(er => ({ ...er, captcha: '' })); }}
                  type="number" placeholder="Enter the answer"
                  className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors
                    ${errors.captcha ? 'border-rose-400 bg-rose-50' : 'border-gray-200'}`} />
                {errors.captcha && <p className="text-xs text-rose-500 mt-0.5">{errors.captcha}</p>}
              </div>

              {/* Submit */}
              <button type="submit" disabled={loading}
                className="w-full py-3 text-white font-semibold rounded-xl text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
                style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
                {loading
                  ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Creating Account…</>
                  : 'Create Master Admin Account'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/admin/login"
                className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors">
                <ArrowLeft size={13} /> Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
